import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { surveysApi } from '@/api/surveys';
import { useModalContext } from '@/components/ui/modal-provider';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { builderReducer, initialBuilderState } from './builderReducer';
import {
    cloneSnapshot,
    countQuestions,
    defaultSnapshot,
    extractApiErrorMessage,
    getActiveSectionId,
    normalizeSnapshotForBuilder,
    normalizeSnapshotForSave,
    validatePublishableSnapshot,
} from './builderUtils';
import ImportGoogleFormsDialog from './components/ImportGoogleFormsDialog';
import type { SaveStatus } from '@/components/system/SaveIndicator';

const snapshotSignature = (value: unknown) => JSON.stringify(value);

export const useCustomSurveyBuilder = (surveyId?: string) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { openModal, closeModal } = useModalContext();
    const confirmDelete = useConfirmDelete();
    const [state, dispatch] = useReducer(builderReducer, initialBuilderState);
    const [savedSnapshot, setSavedSnapshot] = useState(initialBuilderState.snapshot);
    const [loadedVersionId, setLoadedVersionId] = useState<string | null>(null);
    const [showPublishIssues, setShowPublishIssues] = useState(false);
    // True once the current editing session has persisted a draft via autosave.
    // Lets the UI reflect "unpublished changes" without refetching the survey
    // (which would risk re-hydrating and clobbering in-progress edits).
    const [hasAutosavedDraft, setHasAutosavedDraft] = useState(false);
    // Set when the last autosave attempt failed; drives the "Erro ao salvar" state.
    const [autosaveFailed, setAutosaveFailed] = useState(false);

    // Always points at the latest editor snapshot so the autosave mutation
    // persists current content regardless of when its closure was created.
    const snapshotRef = useRef(state.snapshot);
    snapshotRef.current = state.snapshot;

    const customSurveyQuery = useQuery({
        queryKey: ['surveys', 'custom', surveyId],
        queryFn: () => surveysApi.getCustom(surveyId!),
        enabled: !!surveyId,
    });

    const customSurvey = customSurveyQuery.data;
    const activeVersion = customSurvey?.draft ?? customSurvey?.published;
    const hasPublishedVersion = Boolean(customSurvey?.published);
    const hasDraftVersion = Boolean(customSurvey?.draft);

    useEffect(() => {
        if (!customSurvey || !activeVersion) return;
        if (loadedVersionId === activeVersion.id) return;

        const initialSnapshot = normalizeSnapshotForBuilder(activeVersion.schema);
        dispatch({ type: 'hydrate', snapshot: initialSnapshot, activeQuestionId: null });
        setSavedSnapshot(cloneSnapshot(initialSnapshot));
        setLoadedVersionId(activeVersion.id);
    }, [activeVersion, customSurvey, loadedVersionId]);

    const refreshQueries = async () => {
        await queryClient.invalidateQueries({ queryKey: ['surveys'] });
        await queryClient.invalidateQueries({ queryKey: ['surveys', 'custom'] });
        await queryClient.invalidateQueries({ queryKey: ['surveys', 'custom', surveyId] });
    };

    const currentSignature = useMemo(() => snapshotSignature(state.snapshot), [state.snapshot]);
    const savedSignature = useMemo(() => snapshotSignature(savedSnapshot), [savedSnapshot]);
    const isDirty = currentSignature !== savedSignature;
    const publishIssues = useMemo(
        () => validatePublishableSnapshot(state.snapshot),
        [state.snapshot],
    );
    const totalQuestions = useMemo(() => countQuestions(state.snapshot), [state.snapshot]);
    const activeSectionId = useMemo(
        () => getActiveSectionId(state.snapshot, state.activeQuestionId),
        [state.activeQuestionId, state.snapshot],
    );

    useEffect(() => {
        if (publishIssues.length === 0) {
            setShowPublishIssues(false);
        }
    }, [publishIssues.length]);

    // Silent autosave. Persists the current editor snapshot as a draft and
    // advances the "clean" baseline to exactly what was sent, so the editor
    // state is never re-hydrated (no cursor jumps, no lost keystrokes). If the
    // user kept typing during the request, the baseline won't match the new
    // snapshot and the debounced effect simply schedules another save.
    const autosaveMutation = useMutation({
        mutationFn: async () => {
            const sent = cloneSnapshot(snapshotRef.current);
            await surveysApi.saveCustomDraft(surveyId!, normalizeSnapshotForSave(sent));
            return sent;
        },
        onMutate: () => {
            setAutosaveFailed(false);
        },
        onSuccess: (sent) => {
            setSavedSnapshot(sent);
            setHasAutosavedDraft(true);
        },
        onError: (error) => {
            setAutosaveFailed(true);
            toast.error(
                extractApiErrorMessage(
                    error,
                    'Não foi possível salvar automaticamente. Verifique sua conexão.',
                ),
            );
        },
    });

    const publishMutation = useMutation({
        mutationFn: async () => {
            const snapshot = normalizeSnapshotForSave(state.snapshot);
            await surveysApi.saveCustomDraft(surveyId!, snapshot);
            return surveysApi.publishCustom(surveyId!);
        },
        onSuccess: async (data) => {
            const snapshot = normalizeSnapshotForBuilder(
                data.published?.schema ?? data.draft?.schema ?? defaultSnapshot(),
            );

            toast.success('Formulário publicado.');
            setShowPublishIssues(false);
            setHasAutosavedDraft(false);
            setSavedSnapshot(cloneSnapshot(snapshot));
            setLoadedVersionId(data.published?.id ?? data.draft?.id ?? null);
            dispatch({ type: 'hydrate', snapshot, activeQuestionId: null });
            await refreshQueries();
        },
        onError: (error) => {
            toast.error(extractApiErrorMessage(error, 'Revise o formulário antes de publicar.'));
        },
    });

    const discardDraftMutation = useMutation({
        mutationFn: () => surveysApi.deleteCustomDraft(surveyId!),
        onSuccess: async (data) => {
            const snapshot = normalizeSnapshotForBuilder(
                data.published?.schema ?? defaultSnapshot(),
            );

            toast.success('Rascunho descartado.');
            setHasAutosavedDraft(false);
            setSavedSnapshot(cloneSnapshot(snapshot));
            setLoadedVersionId(data.published?.id ?? null);
            dispatch({ type: 'hydrate', snapshot, activeQuestionId: null });
            await refreshQueries();
        },
        onError: (error) => {
            toast.error(extractApiErrorMessage(error, 'Não foi possível descartar o rascunho.'));
        },
    });

    const isMutatingVersion = publishMutation.isPending || discardDraftMutation.isPending;

    // A fresh edit clears a prior failure so the debounced save can retry.
    useEffect(() => {
        setAutosaveFailed(false);
    }, [currentSignature]);

    // Debounced autosave: whenever the editor is dirty and idle for ~1s, persist
    // a draft. Never runs while publishing/discarding, and coalesces so only one
    // save is in flight at a time. A persistent failure pauses the loop until the
    // next edit (which clears the flag) or an explicit retry.
    useEffect(() => {
        if (!surveyId || !isDirty || autosaveFailed) return;
        if (autosaveMutation.isPending || isMutatingVersion) return;

        const timer = setTimeout(() => {
            autosaveMutation.mutate();
        }, 1000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentSignature,
        isDirty,
        surveyId,
        autosaveFailed,
        autosaveMutation.isPending,
        isMutatingVersion,
    ]);

    const restorePublishedSnapshot = () => {
        const snapshot = normalizeSnapshotForBuilder(
            customSurvey?.published?.schema ?? defaultSnapshot(),
        );

        setHasAutosavedDraft(false);
        setSavedSnapshot(cloneSnapshot(snapshot));
        setLoadedVersionId(customSurvey?.published?.id ?? null);
        dispatch({ type: 'hydrate', snapshot, activeQuestionId: null });
        toast.success('Rascunho descartado.');
    };

    const requestBack = async () => {
        // Flush any pending edits before leaving. Autosave normally has this
        // covered, but flushing guards against navigating away inside the debounce
        // window. Errors are already surfaced by the mutation's onError toast.
        if (isDirty && surveyId) {
            try {
                await autosaveMutation.mutateAsync();
            } catch {
                // keep navigating; the edits remain in the last-saved draft
            }
        }

        navigate('/surveys');
    };

    const requestPublish = () => {
        if (publishIssues.length > 0) {
            setShowPublishIssues(true);
            toast.warning('Revise os itens pendentes antes de publicar.');
            return;
        }

        setShowPublishIssues(false);
        openModal({
            variant: 'info',
            title: 'Publicar formulário?',
            description: 'Uma nova versão será criada para envios futuros.',
            content:
                'As respostas e links já enviados continuam ligados à versão anterior. Ao publicar, este rascunho passa a ser a versão usada nos próximos envios.',
            footer: {
                closeText: 'Cancelar',
                acceptText: 'Publicar',
                onAccept: (close) => {
                    close();
                    publishMutation.mutate();
                },
            },
        });
    };

    const dismissPublishIssues = () => {
        setShowPublishIssues(false);
    };

    const requestDiscardDraft = () => {
        openModal({
            variant: 'warning',
            title: 'Descartar rascunho?',
            description: 'As alterações não publicadas serão removidas.',
            content:
                'O editor voltará para a última versão publicada. Links e respostas já concluídas não serão alterados.',
            footer: {
                closeText: 'Cancelar',
                acceptText: 'Descartar rascunho',
                acceptVariant: 'destructive',
                onAccept: (close) => {
                    close();
                    if (hasDraftVersion || hasAutosavedDraft) {
                        discardDraftMutation.mutate();
                    } else {
                        restorePublishedSnapshot();
                    }
                },
            },
        });
    };

    const requestImportGoogleForms = () => {
        openModal({
            title: 'Importar do Google Forms',
            size: 'md',
            content: (
                <ImportGoogleFormsDialog
                    hasContent={totalQuestions > 0}
                    onClose={closeModal}
                    onConfirm={(snapshot) => {
                        closeModal();
                        // savedSnapshot is intentionally untouched: the import
                        // lands as unsaved changes for the user to review.
                        dispatch({
                            type: 'hydrate',
                            snapshot: normalizeSnapshotForBuilder(snapshot),
                            activeQuestionId: null,
                        });
                        toast.success('Formulário importado. Revise e salve o rascunho.');
                    }}
                />
            ),
        });
    };

    const requestDeleteSection = (sectionId: string) => {
        const section = state.snapshot.sections.find((item) => item.id === sectionId);
        if (!section || state.snapshot.sections.length <= 1) return;

        if (section.questions.length === 0) {
            dispatch({ type: 'deleteSection', sectionId });
            return;
        }

        confirmDelete({
            title: 'Excluir seção?',
            description: 'Todas as perguntas desta seção serão removidas do rascunho.',
            confirmText: 'Excluir seção',
            onConfirm: (close) => {
                close();
                dispatch({ type: 'deleteSection', sectionId });
            },
        });
    };

    // Autosave is intentionally excluded: it fires silently while typing and must
    // not disable the toolbar. Only the deliberate publish/discard actions do.
    const isBusy = publishMutation.isPending || discardDraftMutation.isPending;
    const hasUnpublishedEdits = hasDraftVersion || hasAutosavedDraft || isDirty;
    const canDiscardDraft = hasPublishedVersion && hasUnpublishedEdits;

    const saveStatus: SaveStatus = autosaveFailed
        ? 'error'
        : autosaveMutation.isPending
          ? 'saving'
          : isDirty
            ? 'unsaved'
            : 'saved';
    const retrySave = () => autosaveMutation.mutate();

    return {
        activeSectionId,
        canDiscardDraft,
        customSurvey,
        dismissPublishIssues,
        dispatch,
        hasPublishedVersion,
        hasUnpublishedEdits,
        isBusy,
        isLoading: customSurveyQuery.isLoading,
        publishIssues,
        requestBack,
        requestDeleteSection,
        requestDiscardDraft,
        requestImportGoogleForms,
        requestPublish,
        retrySave,
        saveStatus,
        showPublishIssues,
        state,
        totalQuestions,
    };
};
